/**
 * BL-75 (D-210) · Touch-first placement — the SINGLE source of "feel".
 *
 * Every tunable for the unified drag-and-drop placement lives here (TOUCH_PLACEMENT_SPEC §1),
 * so the touch feel is adjustable in one place without hunting through handlers (concept §17).
 * Values are the spec defaults; ranges are documented inline.
 *
 * These are pure interaction tunables (CSS/screen-px thresholds + booleans). They never touch
 * the sim — no balance, no RNG, no determinism surface.
 */
export interface PlacementTuning {
  /** Movement below this (CSS px) = a "tap", above = a "drag". ONLY disambiguates a chip
   *  tap-to-arm from a chip drag-to-carry — never the placement itself. (8–14, default 10.) */
  readonly TAP_SLOP_PX: number;
  /** Touch/pen only. The aim point sits this far ABOVE the contact so the finger never covers
   *  the target cell (a fingertip ≈ 44–48 px + margin). 0 for mouse. (44–80, default 56.) */
  readonly AIM_OFFSET_PX: number;
  /** Floor for the offset when zoomed in, in on-screen cells, so it still clears a finger:
   *  effective = max(AIM_OFFSET_PX, onScreenCellPx * this). (0.8–1.5, default 1.0.) */
  readonly AIM_OFFSET_MIN_CELLS: number;
  /** After a commit the placeable stays armed for fast consecutive building. */
  readonly STICKY_ARM: boolean;
  /** `setPointerCapture` on grab so the drag tracks off-board (rail → board, off-board release). */
  readonly POINTER_CAPTURE: boolean;
  /** D-332 PERF — a cell-BOUNDARY jitter (finger/cursor resting exactly on a cell edge)
   *  can re-cross the SAME two cells many times a second; a bounce straight back to the
   *  cell just left, inside this many ms, reuses the last computed validity instead of
   *  re-running canPlaceAt's A* + the reroute preview. Never applied to the forced
   *  commit-time (UP) re-check, so it can't weaken the actual valid&&afford gate. */
  readonly BOUNDARY_JITTER_MS: number;
}

export const PLACEMENT_TUNING: PlacementTuning = {
  TAP_SLOP_PX: 10,
  // CR-1 (reverses the earlier "directly under the finger" call): lift the aim a
  // touch ABOVE the contact so the tower/ghost peeks out over the fingertip for
  // precise placement. 48 CSS px clears the ~22–28 px finger-pad occlusion
  // radius with a small visible margin — "slightly above, not too much" — while
  // sitting under the comfortable 44–56 px range (Offset-Cursor / Shift, CHI'07;
  // MIT Touch-Lab finger-pad 40–57 px). Touch/pen only — mouse stays precise
  // (clientToCell applies the offset for touch/pen, 0 for mouse). 1.0-cell floor
  // keeps the lift ≥ one on-screen cell when zoomed in (load-bearing for pinch).
  AIM_OFFSET_PX: 48,
  AIM_OFFSET_MIN_CELLS: 1.0,
  STICKY_ARM: true,
  POINTER_CAPTURE: true,
  BOUNDARY_JITTER_MS: 60,
};

/** Haptic trigger points (SPEC §7) — progressive enhancement, fired only where supported.
 *  D-332 — `tapRejected` is a NEW, distinct cue for a touch tap that never exceeded the
 *  tap slop (drag-only-on-touch): it must not feel like `invalid` (an illegal-cell
 *  rejection) — it is a "nothing happened, try a real drag" cue, not a punishment. */
export type HapticKind = "grab" | "validEnter" | "invalid" | "commit" | "tapRejected";

/** Vibration patterns (ms) per trigger. iOS web has no Vibration API → effectively Android-only. */
export const HAPTIC_PATTERNS: Record<HapticKind, number | number[]> = {
  grab: 10,
  validEnter: 8,
  invalid: [6, 40, 6],
  commit: 18,
  tapRejected: 5,
};
